import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Card, Badge } from "@/components/Primitives";
import { Progress } from "@/components/ui/progress";
import { Target, ShieldCheck, Users2, Trophy, BookOpen, LifeBuoy, FileCheck2 } from "lucide-react";

const responsibilities = [
  { icon: ShieldCheck, t: "SOP Process Training & Compliance Checks" },
  { icon: Users2, t: "1-on-1 Coaching Sessions" },
  { icon: BookOpen, t: "Group / Department Training Sessions" },
  { icon: LifeBuoy, t: "Low-Performer Support & Remediation" },
  { icon: FileCheck2, t: "Knowledge & Situational Assessments" },
  { icon: Target, t: "Cross-Department Readiness Reporting" },
  { icon: Trophy, t: "Team Member Ranking & Feedback Delivery" },
];

export default function Goals() {
  const { settings } = useApp();
  const [dash, setDash] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setDash(r.data)).catch(() => {});
  }, []);

  const k = dash?.kpis;
  const target = settings?.low_performer_threshold || 60;

  const scorecard = k ? [
    { label: "Avg Team Readiness", actual: k.avg_readiness, target: 90, unit: "%" },
    { label: "SOP-Compliant Departments", actual: k.fully_compliant_departments, target: null, unit: "" },
    { label: "Members Below Standard", actual: k.below_standard, target: 0, unit: "", invert: true },
    { label: "Assessments Pending", actual: k.assessments_pending, target: 0, unit: "", invert: true },
    { label: "Sessions Delivered (period)", actual: k.sessions_completed, target: null, unit: "" },
  ] : [];

  const rag = (row) => {
    if (row.target === null) return "gold";
    if (row.invert) return row.actual <= row.target ? "green" : "red";
    return row.actual >= row.target ? "green" : row.actual >= target ? "amber" : "red";
  };

  return (
    <div>
      <PageHeader title="Goals & Objectives" subtitle="Ensure every department is SOP-ready, knowledge-ready and test-ready." />

      <Card className="p-8 mb-6 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#C9A24B]/10 blur-3xl" />
        <p className="text-xs uppercase tracking-[0.2em] text-[#C9A24B] mb-3">Mission</p>
        <p className="text-xl sm:text-2xl font-light font-display tracking-tight text-white max-w-3xl leading-snug">
          Ensure every department is SOP-ready, knowledge-ready and test-ready — and that no team member is left to
          struggle without a documented path to improvement.
        </p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-5">Live KPI Scorecard — Target vs Actual</p>
          <div className="space-y-5">
            {scorecard.map((row, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-slate-300">{row.label}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-white text-sm">{row.actual}{row.unit}</span>
                    {row.target !== null && (
                      <span className="text-xs text-slate-500">/ target {row.target}{row.unit}</span>
                    )}
                    <Badge tone={rag(row)}>{rag(row) === "green" ? "On Target" : rag(row) === "amber" ? "Watch" : rag(row) === "red" ? "Attention" : "Tracking"}</Badge>
                  </div>
                </div>
                {row.unit === "%" && <Progress value={row.actual} className="h-1.5 bg-slate-800" />}
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-5">Core Responsibilities</p>
          <div className="space-y-3">
            {responsibilities.map((r, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-md bg-[#1E293B] flex items-center justify-center shrink-0">
                  <r.icon className="h-4 w-4 text-[#C9A24B]" />
                </div>
                <span className="text-sm text-slate-300">{r.t}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
